import { useCallback, useEffect, useState } from "react";
import { GiSkullCrossedBones } from "react-icons/gi";
import { LuCopyPlus } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import ModalCadastroAnimal from "../../components/modais/ModalCadastroAnimal";
import { api } from "../../services/api";
import { repositorioAnimal, repositorioMortalidade } from "../../sync/repositories/pecuaria";

const AJUDA = {
  titulo: "Registro de Mortalidade",
  descricao:
    "Registre aqui o óbito de um animal do plantel. Ao salvar, o sistema muda automaticamente o status do animal para 'Morto', removendo-o das listas de seleção de animais ativos em outras telas (Pesagem, Manejo, etc.).",
  secoes: [
    {
      titulo: "Dados do Óbito",
      campos: [
        {
          nome: "Animal",
          obrigatorio: true,
          explicacao: "Só aparecem animais com status Ativo. Se o animal já morreu, ele não aparece mais aqui — confira o cadastro de Animais se precisar corrigir algo.",
        },
        {
          nome: "Data da Morte",
          obrigatorio: true,
          explicacao: "Data em que o óbito ocorreu (ou foi constatado).",
        },
        {
          nome: "Causa da Morte",
          obrigatorio: true,
          explicacao: "Descrição curta do motivo (ex.: doença, acidente, predação).",
        },
        {
          nome: "Observações",
          obrigatorio: false,
          explicacao: "Detalhes adicionais: sintomas observados, tratamento tentado, contexto do ocorrido.",
        },
      ],
    },
  ],
  dicas: [
    "Cadastrar, editar ou excluir um registro de óbito exige conexão com a internet — apenas a consulta funciona offline.",
    "Excluir um registro de óbito não reverte automaticamente o status do animal para 'Ativo' — se for o caso, corrija o status diretamente no cadastro do Animal.",
  ],
};

export default function GestaoMortalidade() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [obitos, setObitos] = useState([]);
  const [obitosFiltrados, setObitosFiltrados] = useState([]);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  
  // Dados para os selects
  const [animais, setAnimais] = useState([]);
  const [mostrarModalAnimal, setMostrarModalAnimal] = useState(false);
  
  const [filtros, setFiltros] = useState({
    busca: "",
    data_inicio: "",
    data_fim: ""
  });
  
  const [formData, setFormData] = useState({
    animal: "",
    data_morte: "",
    causa: "",
    observacoes: "",
  });

  // Carregar animais (apenas ativos)
  const carregarAnimais = useCallback(async () => {
    try {
      const response = await repositorioAnimal.listar();
      const dados = response.data.results || response.data;
      const animaisArray = Array.isArray(dados) ? dados : [];
      // Filtra apenas animais ativos (status "A")
      const animaisAtivos = animaisArray.filter(animal => animal.status === "A");
      setAnimais(animaisAtivos);
    } catch (error) {
      console.error("Erro ao carregar animais:", error);
      setAnimais([]);
    }
  }, []);

  // Carregar óbitos
  const carregarObitos = useCallback(async () => {
    try {
      setCarregandoLista(true);
      const response = await repositorioMortalidade.listar();
      const dados = response.data.results || response.data;
      setObitos(Array.isArray(dados) ? dados : []);
      setObitosFiltrados(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar óbitos:", error);
      alert("Erro ao carregar lista de óbitos");
    } finally {
      setCarregandoLista(false);
    }
  }, []);

  useEffect(() => {
    carregarObitos();
    carregarAnimais();
  }, [carregarObitos, carregarAnimais]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validarFormulario = () => {
    if (!formData.animal) {
      alert("Selecione um animal.");
      return false;
    }
    if (!formData.data_morte) {
      alert("Informe a data da morte.");
      return false;
    }
    if (!formData.causa.trim()) {
      alert("Informe a causa da morte.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validarFormulario()) return;
    
    setLoading(true);

    const payload = {
      animal: formData.animal,
      data_morte: formData.data_morte,
      causa: formData.causa,
      observacoes: formData.observacoes || "",
    };

    try {
      if (editando) {
        await api.put(`/pecuaria/mortalidade/${editandoId}/`, payload);
        alert("Óbito atualizado com sucesso!");
        handleCancel();
        carregarObitos();
      } else {
        await api.post("/pecuaria/mortalidade/", payload);
        
        await api.patch(`/pecuaria/animal/${formData.animal}/`, {
          status: 'M'
        });
        
        alert("Óbito registrado com sucesso!");
        handleCancel();
        carregarObitos();
        carregarAnimais();
      }
    } catch (error) {
      console.error("Erro:", error);
      const data = error.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const erros = Object.values(data).flat();
        alert(erros.join("\n"));
      } else if (Array.isArray(data)) {
        alert(data.join("\n"));
      } else {
        alert("Erro ao salvar. Verifique os dados e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (obito) => {
    setFormData({
      animal: obito.animal?.id || obito.animal || "",
      data_morte: obito.data_morte || "",
      causa: obito.causa || "",
      observacoes: obito.observacoes || "",
    });
    setEditando(true);
    setEditandoId(obito.id);
    setMostrarFormulario(true);
    
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleNovoObito = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setFormData({
      animal: "",
      data_morte: "",
      causa: "",
      observacoes: "",
    });
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
  };

  const handleExcluir = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este registro de óbito? Esta ação não pode ser desfeita.")) {
      return;
    }
    
    try {
      await api.delete(`/pecuaria/mortalidade/${id}/`);
      alert("Registro de óbito excluído com sucesso!");
      carregarObitos();
      carregarAnimais(); // Recarrega animais para atualizar a lista
      
      if (editandoId === id) {
        handleCancel();
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
      alert("Erro ao excluir registro de óbito.");
    }
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtrados = Array.isArray(obitos) ? [...obitos] : [];
    
    if (filtros.busca) {
      const buscaLower = filtros.busca.toLowerCase();
      filtrados = filtrados.filter(o => 
        o.animal_sisbov?.toLowerCase().includes(buscaLower) ||
        o.animal_nome?.toLowerCase().includes(buscaLower) ||
        o.causa?.toLowerCase().includes(buscaLower)
      );
    }
    
    if (filtros.data_inicio) {
      filtrados = filtrados.filter(o => o.data_morte >= filtros.data_inicio);
    }
    
    if (filtros.data_fim) {
      filtrados = filtrados.filter(o => o.data_morte <= filtros.data_fim);
    }
    
    setObitosFiltrados(filtrados);
  };

  const limparFiltros = () => {
    setFiltros({ busca: "", data_inicio: "", data_fim: "" });
    setObitosFiltrados(Array.isArray(obitos) ? obitos : []);
  };

  const formatarData = (data) => {
    if (!data) return "—";
    return new Date(data).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  };

  const getAnimalIdentificacao = (obito) => {
    const nome = obito.animal_nome || obito.animal?.nome || "Sem nome";
    const sisbov = obito.animal_sisbov || obito.animal?.sisbov || obito.animal?.numero_identificacao || "";
    
    if (sisbov) {
      return `${sisbov} - ${nome}`;
    }
    return nome;
  };

  const getAnimalDisplay = (animal) => {
    const identificacao = animal.sisbov || animal.numero_identificacao || `ID: ${animal.id}`;
    const nome = animal.nome || "Sem nome";
    return `${identificacao} - ${nome}`;
  };

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">Gestão de Mortalidade <GiSkullCrossedBones size={38} /></h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovoObito}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Registrar Óbito
            </button>
          )}
        </div>
        
        {/* Formulário de Cadastro/Edição */}
        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {editando ? "Editar Registro de Óbito" : "Registrar Óbito"}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {/* Linha 1: Animal, Data e Causa */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Animal *</label>
                    <div className="flex items-center gap-2">
                      <select
                        name="animal"
                        value={formData.animal}
                        onChange={handleChange}
                        className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        required
                      >
                        <option value="">Selecione um animal...</option>
                        {Array.isArray(animais) && animais.map(animal => (
                          <option key={animal.id} value={animal.id}>
                            {getAnimalDisplay(animal)}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setMostrarModalAnimal(true)}
                        title="Cadastrar novo animal"
                        className="p-2 hover:bg-gray-200 rounded"
                      >
                        <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Data da Morte *</label>
                    <input
                      type="date"
                      name="data_morte"
                      value={formData.data_morte}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      required
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Causa da Morte *</label>
                    <input
                      type="text"
                      name="causa"
                      value={formData.causa}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      placeholder="Ex: Doença, Acidente, Predação, etc."
                      required
                    />
                  </div>
                </div>

                {/* Linha 2: Observações */}
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Observações</label>
                  <textarea
                    name="observacoes"
                    value={formData.observacoes}
                    onChange={handleChange}
                    rows={4}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    placeholder="Informações adicionais sobre o ocorrido, sintomas, tratamento realizado, etc..."
                  />
                </div>

                {/* Botões do formulário */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300"
                  >
                    {loading ? "Salvando..." : (editando ? "Atualizar" : "Registrar")}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
        
        {!mostrarFormulario && (
          <>
            {/* Seção de Filtros */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filtros de Busca
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Buscar por Animal ou Causa</label>
                  <input
                    type="text"
                    placeholder="Digite o SISBOV, nome do animal ou causa..."
                    value={filtros.busca}
                    onChange={(e) => setFiltros({...filtros, busca: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>
                
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Data Início</label>
                  <input
                    type="date"
                    value={filtros.data_inicio}
                    onChange={(e) => setFiltros({...filtros, data_inicio: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>
                
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Data Fim</label>
                  <input
                    type="date"
                    value={filtros.data_fim}
                    onChange={(e) => setFiltros({...filtros, data_fim: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-4">
                <button
                  onClick={aplicarFiltros}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
                >
                  Aplicar Filtros
                </button>
                <button
                  onClick={limparFiltros}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>

            {/* Tabela de Óbitos */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <p className="text-gray-600">
                  {Array.isArray(obitosFiltrados) ? obitosFiltrados.length : 0} registro(s) de óbito encontrado(s)
                </p>
              </div>
              
              {carregandoLista ? (
                <div className="p-8 text-center text-gray-500">Carregando...</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Código</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Data</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Animal</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Causa</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Observações</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(obitosFiltrados) && obitosFiltrados.map((obito) => (
                        <tr key={obito.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                          <td className="p-3 text-sm font-mono">{obito.id}</td>
                          <td className="p-3 text-sm">{formatarData(obito.data_morte)}</td>
                          <td className="p-3 text-sm font-medium">
                            {getAnimalIdentificacao(obito)}
                          </td>
                          <td className="p-3 text-sm">
                            <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">
                              {obito.causa}
                            </span>
                          </td>
                          <td className="p-3 text-sm max-w-xs truncate">{obito.observacoes || "—"}</td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditar(obito)}
                                className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Editar"
                              >
                                Editar
                              </button>
                              <button
                                onClick={() => handleExcluir(obito.id)}
                                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Excluir"
                              >
                                Excluir
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      
                      {(!Array.isArray(obitosFiltrados) || obitosFiltrados.length === 0) && (
                        <tr>
                          <td colSpan="6" className="p-8 text-center text-gray-500">
                            {filtros.busca || filtros.data_inicio || filtros.data_fim
                              ? "Nenhum registro encontrado com os filtros selecionados."
                              : "Nenhum registro de óbito cadastrado."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <ModalCadastroAnimal
        isOpen={mostrarModalAnimal}
        onClose={() => setMostrarModalAnimal(false)}
        onSuccess={async (novo) => {
          await carregarAnimais();
          setFormData((prev) => ({ ...prev, animal: novo.id }));
        }}
      />
    </Layout>
  );
}